import type {
  LaunchSecureDto,
  PatinfoPhiDto,
  PatinfoProfileDto,
  PatinfoPutRequestDto,
} from '../../types/api/patinfo';

export function applyOptimisticProfileUpdate(
  profile: PatinfoProfileDto,
  payload: PatinfoPutRequestDto
): PatinfoProfileDto {
  const nextPhi: PatinfoPhiDto[] = payload.phi ? payload.phi : profile.phi;

  return {
    ...profile,
    ...(payload.patientid ? { patientid: payload.patientid } : {}),
    ...(payload.status ? { status: payload.status } : {}),
    ...(payload.modified ? { modified: payload.modified } : {}),
    phi: nextPhi,
  };
}

export function applyOptimisticProfileListUpdate(
  profiles: PatinfoProfileDto[],
  payload: PatinfoPutRequestDto
): PatinfoProfileDto[] {
  const targetPatcode = payload.patcode;
  const targetPatientId = payload.patientid;

  return profiles.map((profile) => {
    const matchesPatcode = !!targetPatcode && profile.patcode === targetPatcode;
    const matchesPatientId = !!targetPatientId && profile.patientid === targetPatientId;

    return matchesPatcode || matchesPatientId
      ? applyOptimisticProfileUpdate(profile, payload)
      : profile;
  });
}

/**
 * Appends a backend-confirmed launch to the matching profile's `launches`.
 * launch_secure_put always inserts a new launch record (never updates one in
 * place), so this appends rather than replacing/merging by SSKey.
 */
export function applyLaunchToProfileList(
  profiles: PatinfoProfileDto[],
  targetPatcode: string | undefined,
  targetPatientId: string | undefined,
  launch: LaunchSecureDto
): PatinfoProfileDto[] {
  return profiles.map((profile) => {
    const matchesPatcode = !!targetPatcode && profile.patcode === targetPatcode;
    const matchesPatientId = !!targetPatientId && profile.patientid === targetPatientId;

    if (!matchesPatcode && !matchesPatientId) {
      return profile;
    }

    return {
      ...profile,
      launches: [...(profile.launches ?? []), launch],
    };
  });
}

/**
 * Removes a backend-confirmed-cancelled launch (identified by SSKey) from the
 * matching profile's `launches`, mirroring the backend delete on cancel.
 */
export function removeLaunchFromProfileList(
  profiles: PatinfoProfileDto[],
  targetPatcode: string | undefined,
  targetPatientId: string | undefined,
  sskey: string
): PatinfoProfileDto[] {
  return profiles.map((profile) => {
    const matchesPatcode = !!targetPatcode && profile.patcode === targetPatcode;
    const matchesPatientId = !!targetPatientId && profile.patientid === targetPatientId;

    if (!matchesPatcode && !matchesPatientId) {
      return profile;
    }

    return {
      ...profile,
      launches: (profile.launches ?? []).filter((launch) => launch.SSKey !== sskey),
    };
  });
}
